import AriaUtils from '../../utils/aria-utils';
import { bindMethods, matches } from './util';

const selector = {
  fixedLeft: '.zm-table__fixed',
  fixedRight: '.zm-table__fixed-right',
  header: '.zm-table__header',
  body: '.zm-table__body',
  row: 'tr:not(.zm-table__row--hidden):not(.zm-table__expanded-tr)',
  cell: 'td:not(.is-hidden),th:not(.is-hidden)',
  tabindex: '[tabindex]:not([disabled])',
  settingBtn: '.zm-table__column-setting-btn',
  stickySettingBtn: '.zm-sticky-fixed.zm-table .zm-table__column-setting-btn',
  stickyHeader: '.zm-sticky-fixed.zm-table .zm-table__header-wrapper',
  stickyFixedLeftHeader: '.zm-sticky-fixed.zm-table.fixed-left .zm-table__fixed-header-wrapper',
  stickyFixedRightHeader: '.zm-sticky-fixed.zm-table.fixed-right .zm-table__fixed-header-wrapper'
};

export default class Grid {
  constructor(gridNode, isStickyHeader, flip) {
    this.gridNode = gridNode;
    this.isStickyHeader = isStickyHeader;
    this.flip = flip;
    this.focusedRow = 0;
    this.focusedCol = 0;
    this.focuesedZIndex = 0;

    bindMethods(this, 'checkFocusChange', 'focusClickedCell');

    this.init();
  }

  init() {
    this.fixedLeftEl = this.gridNode.querySelector(selector.fixedLeft);
    this.fixedRightEl = this.gridNode.querySelector(selector.fixedRight);
    this.fixedLeftBodyEl = this.fixedLeftEl && this.fixedLeftEl.querySelector(selector.body);
    this.fixedRightBodyEl = this.fixedRightEl && this.fixedRightEl.querySelector(selector.body);

    this.bodyEl = this.gridNode.querySelector(selector.body);

    this.initHeaderEls();

    this.resetTabIndex();

    this.setupFocusGrid();

    if (!this.setFocusPointer(this.focusedRow, this.focusedCol, this.focuesedZIndex)) {
      this.focusedRow = 0;
      this.focusedCol = 0;
      this.focuesedZIndex = 0;
      while (!this.setFocusPointer(this.focusedRow, this.focusedCol, this.focuesedZIndex) && this.focusedRow < this.grid.length) {
        this.focusedCol++;
        if (this.focusedCol > this.grid[this.focusedRow].length - 1) {
          this.focusedCol = 0;
          this.focusedRow++;
        }
      }
    }

    this.registerEvents();
  }

  rebuild(isStickyHeader, flip) {
    this.isStickyHeader = isStickyHeader;
    this.flip = flip;
    this.init();
  }

  initHeaderEls() {
    if (this.isStickyHeader) {
      this.headerEl = document.querySelector(selector.stickyHeader);
      this.fixedLeftHeaderEl = document.querySelector(selector.stickyFixedLeftHeader);
      this.fixedRightHeaderEl = document.querySelector(selector.stickyFixedRightHeader);
      this.settingBtnEl = document.querySelector(selector.stickySettingBtn);
    } else {
      this.headerEl = this.gridNode.querySelector(selector.header);
      this.fixedLeftHeaderEl = this.fixedLeftEl && this.fixedLeftEl.querySelector(selector.header);
      this.fixedRightHeaderEl = this.fixedRightEl && this.fixedRightEl.querySelector(selector.header);
      this.settingBtnEl = this.gridNode.querySelector(selector.settingBtn);
    }
  }

  resetTabIndex() {
    const matchedEls = this.gridNode.querySelectorAll(selector.tabindex);

    Array.prototype.forEach.call(matchedEls, (el) => {
      if (!el.classList.contains('zm-table__empty-block')) {
        el.setAttribute('tabindex', -1);
      }
    });
  }

  setupFocusGrid() {
    this.grid = [];

    // first header, without flip
    if (!this.flip) {
      let headerRowCells = [];

      // fixed left
      if (this.fixedLeftHeaderEl) {
        headerRowCells = headerRowCells.concat(this.findFocusCells(this.fixedLeftHeaderEl.querySelector(selector.row)));
      }

      // middle
      if (this.headerEl) {
        headerRowCells = headerRowCells.concat(this.findFocusCells(this.headerEl.querySelector(selector.row)));
      }

      // fixed right
      if (this.fixedRightHeaderEl) {
        headerRowCells = headerRowCells.concat(this.findFocusCells(this.fixedRightHeaderEl.querySelector(selector.row)));
      }

      if (this.settingBtnEl) {
        headerRowCells.push([this.settingBtnEl]);
      }

      this.grid.push(headerRowCells);
    }

    // second body

    const bodyRowCount = this.bodyEl.querySelectorAll(selector.row).length;

    for (let i = 0; i < bodyRowCount; i++) {
      let rowCells = [];

      // fixed left
      if (this.fixedLeftBodyEl) {
        rowCells = rowCells.concat(this.findFocusCells(this.fixedLeftBodyEl.querySelectorAll(selector.row)[i]));
      }

      // middle
      if (this.bodyEl) {
        rowCells = rowCells.concat(this.findFocusCells(this.bodyEl.querySelectorAll(selector.row)[i]));
      }
      // fixed right
      if (this.fixedRightBodyEl) {
        rowCells = rowCells.concat(this.findFocusCells(this.fixedRightBodyEl.querySelectorAll(selector.row)[i]));
      }

      this.grid.push(rowCells);
    }
  }

  setFocusPointer(row, col, zIndex) {
    if (!this.isValidCell(row, col, zIndex)) {
      return false;
    }

    if (!isNaN(this.focusedRow) && !isNaN(this.focusedCol) && !isNaN(this.focuesedZIndex) && this.grid[this.focusedRow][this.focusedCol][this.focuesedZIndex]) {
      this.grid[this.focusedRow][this.focusedCol][this.focuesedZIndex].setAttribute('tabindex', -1);
    }

    this.navigationDisabled = matches(this.grid[row][col][zIndex], '[role=menubar]');

    this.grid[row][col][0].setAttribute('tabindex', 0);
    this.focusedRow = row;
    this.focusedCol = col;
    this.focuesedZIndex = zIndex;

    return true;
  }

  registerEvents() {
    this.clearEvents();

    this.gridNode.addEventListener('keydown', this.checkFocusChange);
    this.gridNode.addEventListener('click', this.focusClickedCell);

    this.handleStickySelector((el) => {
      el.addEventListener('keydown', this.checkFocusChange);
      el.addEventListener('click', this.focusClickedCell);
    });
  }

  clearEvents() {
    this.gridNode.removeEventListener('keydown', this.checkFocusChange);
    this.gridNode.removeEventListener('click', this.focusClickedCell);

    this.handleStickySelector((el) => {
      el.removeEventListener('keydown', this.checkFocusChange);
      el.removeEventListener('click', this.focusClickedCell);
    });
  }

  handleStickySelector(cb) {
    if (this.isStickyHeader) {
      const stickySelectors = [selector.stickyHeader, selector.stickyFixedLeftHeader, selector.stickyFixedRightHeader, selector.stickySettingBtn];
      stickySelectors.forEach((selector) => {
        const el = document.querySelector(selector);
        if (el) {
          cb(el);
        }
      });
    }
  }

  checkFocusChange(event) {
    if (!event) {
      return;
    }

    const keyCode = event.keyCode;
    const keys = AriaUtils.keys;

    // this.findFocusedItem(event.target);
    let rowCaret = this.focusedRow;
    let colCaret = this.focusedCol;
    let zIndex = this.focuesedZIndex;
    let nextCell;

    switch (keyCode) {
      case keys.up:
        if (this.navigationDisabled) {
          return;
        }
        nextCell = this.getNextValidCell(0, -1);
        rowCaret = nextCell.row;
        colCaret = nextCell.col;
        zIndex = nextCell.zIndex;
        break;
      case keys.down:
        if (this.navigationDisabled) {
          return;
        }
        nextCell = this.getNextValidCell(0, 1);
        rowCaret = nextCell.row;
        colCaret = nextCell.col;
        zIndex = nextCell.zIndex;
        break;
      case keys.left:
        nextCell = this.getNextValidCell(-1, 0);
        rowCaret = nextCell.row;
        colCaret = nextCell.col;
        zIndex = nextCell.zIndex;
        break;
      case keys.right:
        nextCell = this.getNextValidCell(1, 0);
        rowCaret = nextCell.row;
        colCaret = nextCell.col;
        zIndex = nextCell.zIndex;
        break;
      case keys.home:
        if (event.ctrlKey) {
          rowCaret = 0;
        }
        colCaret = 0;
        zIndex = 0;
        break;
      case keys.end:
        if (event.ctrlKey) {
          rowCaret = this.grid.length - 1;
        }
        colCaret = this.grid[this.focusedRow].length - 1;
        zIndex = 0;
        break;
      default:
        return;
    }

    this.focusCell(rowCaret, colCaret, zIndex);
    event.preventDefault();
    event.stopPropagation();
  }

  getNextValidCell(directionX, directionY) {
    let nextCell = this.getNextCell(this.focusedRow, this.focusedCol, this.focuesedZIndex, directionX, directionY);
    if (!nextCell) {
      return false;
    }
    while (this.isEmptyCell(nextCell.row, nextCell.col)) {
      const currRow = nextCell.row;
      const currCol = nextCell.col;
      const zIndex = nextCell.zIndex;

      nextCell = this.getNextCell(currRow, currCol, zIndex, directionX, directionY);

      if (currRow === nextCell.row && currCol === nextCell.col) {
        // There are no more cells to try if getNextCell returns the current cell
        return false;
      }
    }

    return nextCell;
  }

  getNextCell(currRow, currCol, currZIndex, directionX, directionY) {
    const currentCell = this.grid[currRow][currCol];
    const row = currRow + directionY;
    let zIndex = currZIndex;
    let col = currCol;
    if (currZIndex < currentCell.length - 1 && directionX > 0) {
      zIndex++;
    } else if (currZIndex > 0 && directionX < 0) {
      zIndex--;
    } else {
      col += directionX;
      if (directionX > 0) {
        zIndex = 0;
      } else if (directionX < 0) {
        if (this.isValidCell(row, col, 0)) {
          zIndex = this.grid[row][col].length - 1;
        } else {
          zIndex = 0;
        }
      }
    }
    if (directionY !== 0) {
      zIndex = 0;
    }
    if (this.isEmptyCell(row, col)) {
      return {
        row,
        col,
        zIndex: 0
      };
    }
    if (this.isValidCell(row, col, zIndex)) {
      return {
        row,
        col,
        zIndex
      };
    } else if (this.isValidCell(currRow, currCol, currZIndex)) {
      return {
        row: currRow,
        col: currCol,
        zIndex: currZIndex
      };
    } else {
      return false;
    }
  }

  focusClickedCell(event) {
    const clickedGridCell = this.findClosest(event.target, selector.tabindex);

    if (!clickedGridCell) {
      return;
    }

    for (let row = 0; row < this.grid.length; row++) {
      for (let col = 0; col < this.grid[row].length; col++) {
        for (let z = 0; z < this.grid[row][col].length; z++) {
          if (this.grid[row][col][z] === clickedGridCell) {
            this.focusCell(row, col, z);
            return;
          }
        }
      }
    }
  }

  findClosest(element, selector) {
    if (matches(element, selector)) {
      return element;
    }

    if (matches(element.parentNode, selector)) {
      return element.parentNode;
    }

    return element.querySelector(selector);
  }

  focusCell(row, col, zIndex) {
    if (this.setFocusPointer(row, col, zIndex)) {
      this.grid[row][col][zIndex].focus();
      // mark sure that do not trigger scroll
      this.gridNode.scrollLeft = 0;
    }
  }

  isValidCell(row, col, zIndex) {
    return (
      !isNaN(row) &&
      !isNaN(col) &&
      row >= 0 &&
      col >= 0 &&
      this.grid &&
      this.grid.length &&
      row < this.grid.length &&
      col < this.grid[row].length &&
      zIndex >= 0 &&
      zIndex < this.grid[row][col].length
    );
  }

  isEmptyCell(row, col) {
    return (
      !isNaN(row) &&
      !isNaN(col) &&
      row >= 0 &&
      col >= 0 &&
      this.grid &&
      this.grid.length &&
      row < this.grid.length &&
      col < this.grid[row].length &&
      this.grid[row][col].length === 0
    );
  }

  findFocusCells(row) {
    if (!row) return [];
    const focusCells = [];
    const cells = row.querySelectorAll(selector.cell);
    Array.prototype.forEach.call(cells, (cell) => {
      let colSpan = cell.getAttribute('colspan');
      const focusEls = cell.querySelectorAll(selector.tabindex);
      if (colSpan) {
        while (colSpan--) {
          focusCells.push(focusEls);
        }
      } else {
        focusCells.push(focusEls);
      }
    });
    return focusCells;
  }
}
